// src/pages/equipmentrentaladmin/profile/Profile.jsx

import React, { useState, useRef, useContext, useEffect } from "react";
import { Container, Row, Col, Form, Modal, Nav } from "react-bootstrap";
import {
  MdEdit,
  MdCamera,
  MdSave,
  MdCancel,
  MdPerson,
  MdNotifications,
  MdSettings,
  MdVerifiedUser,
  MdUpload,
  MdBusiness,
  MdPhone,
  MdCreditCard,
} from "react-icons/md";
import { FiClock, FiCheckCircle, FiAlertCircle } from "react-icons/fi";
import RentalAdminLayout from "../../../layout/equipmentrentaladmin/RentalAdminLayout";
import "../../../assets/css/equipmentrentaladmin/profile/Profile.css";
import axios from "axios";
import AppContext from "../../../context/AppContext";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

const Profile = () => {
  const { baseUrl, baseUrlForFile } = useContext(AppContext);
  const navigate = useNavigate();

  // ── States ──
  const [isEditing, setIsEditing] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [isEditingBusiness, setIsEditingBusiness] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [srrentaladmin_id, setSrRentaladmin_id] = useState();
  const [businessname, setBusinessName] = useState();
  const [businessemail, setBusinessEmail] = useState();
  const [description, setDescription] = useState();
  const [aboutthisshop, setAboutThisShop] = useState();
  const [logo, setLogo] = useState();
  const [businessPhone, setBusinessPhone] = useState("");
  const [address, setAddress] = useState();
  const [cnic, setCnic] = useState();
  const [idToUpdateBusinessDetails, setIdToUpdateBusinessDetails] = useState();
  const [activeTab, setActiveTab] = useState("personal");
  const [fullname, setFullname] = useState(null);
  const [email, setEmail] = useState(null);
  const [cphonenumber, setCPhonenumber] = useState(null);
  const [caddress, setCAddress] = useState(null);
  const [image, setImage] = useState(null);
  const [businessDetails, setBusinessDetails] = useState(null);

  // ── KYC States ──
  const [kycStatus, setKycStatus] = useState("pending"); // "pending" | "submitted" | "approved" | "rejected"
  const [uploadedFile, setUploadedFile] = useState(null);

  const pdfInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const avatarInputRef = useRef(null);

  // ── Avatar State ──
  const [avatarPreview, setAvatarPreview] = useState(null);

  // ── Logo States ──
  const [shopLogo, setShopLogo] = useState(null);
  const [shopLogoPreview, setShopLogoPreview] = useState(null);

  // ── Backup states for Cancel ──
  const [businessSettingsBackup, setBusinessSettingsBackup] = useState(null);
  const [shopLogoBackup, setShopLogoBackup] = useState(null);
  const [shopLogoPreviewBackup, setShopLogoPreviewBackup] = useState(null);

  // ================================================================
  // KYC Status Config — same pattern as MechanicKyc
  // ================================================================
  const statusConfig = {
    pending: {
      icon: <FiClock size={22} />,
      title: "Verification Pending",
      desc: "Please submit your documents to complete verification.",
      badge: "Pending Review",
      cls: "status-pending",
    },
    submitted: {
      icon: <FiClock size={22} />,
      title: "Documents Under Review",
      desc: "Your documents are under review. We will notify you once verified.",
      badge: "Under Review",
      cls: "status-submitted",
    },
    approved: {
      icon: <FiCheckCircle size={22} />,
      title: "Verification Approved",
      desc: "Your KYC has been successfully verified. You can now access all features.",
      badge: "Approved",
      cls: "status-approved",
    },
    rejected: {
      icon: <FiAlertCircle size={22} />,
      title: "Verification Rejected",
      desc: "Your documents were not accepted. Please re-upload correct documents.",
      badge: "Rejected",
      cls: "status-rejected",
    },
  };

  const currentStatus = statusConfig[kycStatus];

  // ================================================================
  // API CALLS
  // ================================================================

  const fetchrentalProfile = () => {
    const token = localStorage.getItem("authToken");

    axios
      .get(`${baseUrl}/rentaladmin/profile/fetchrentalProfile`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        console.log(response.data);

        if (response.data.success === true) {
          const rental = response.data.rental;
          const business = response.data.business;

          setFullname(rental.fullname);
          setEmail(rental.email);
          setCPhonenumber(rental.phonenumber);
          setCAddress(rental.address);
          setImage(rental.image);
          setSrRentaladmin_id(rental.id);

          // ── Notification preferences from rental table ──
          setNotifications((prev) => ({
            ...prev,
            emailNotifications: rental.emailNotification == 1,
            smsNotifications: rental.inAppNotification == 1,
          }));

          // ── Set KYC status from rental table ──
          if (rental.verification_status === "verified") {
            setKycStatus("approved");
          } else if (rental.verification_status === "rejected") {
            setKycStatus("rejected");
          } else if (rental.kyc_status === "submitted") {
            setKycStatus("submitted");
          } else {
            setKycStatus("pending");
          }

          if (business) {
            setBusinessDetails(business);
            setIdToUpdateBusinessDetails(business.id);
            setBusinessName(business.businessname);
            setBusinessEmail(business.businessemail);
            setBusinessPhone(business.phonenumber);
            setAddress(business.address);
            setDescription(business.description);
            setAboutThisShop(business.aboutthisshop);
            setCnic(business.cnic);
            setLogo(business.logo);
            if (business.logo) {
              setShopLogoPreview(`${baseUrlForFile}/${business.logo}`);
            }
          }
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const updateRentalProfile = () => {
    const token = localStorage.getItem("authToken");
    const formData = new FormData();
    formData.append("fullname", fullname);
    formData.append("email", email);
    formData.append("phonenumber", cphonenumber);
    formData.append("address", caddress);
    if (avatarFile) {
      formData.append("image", avatarFile);
    }

    axios
      .post(`${baseUrl}/rentaladmin/profile/updateRentalProfile`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        console.log(response.data);
        if (response.data.success === true) {
          fetchrentalProfile();
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const addBussinessDetails = () => {
    const token = localStorage.getItem("authToken");
    const formdata = new FormData();
    formdata.append("srrentaladmin_id", srrentaladmin_id);
    formdata.append("businessname", businessname);
    formdata.append("description", description);
    formdata.append("aboutthisshop", aboutthisshop);
    formdata.append("logo", shopLogo);
    formdata.append("address", address);
    formdata.append("phonenumber", businessPhone);
    formdata.append("businessemail", businessemail);
    formdata.append("cnic", cnic);

    axios
      .post(`${baseUrl}/rentaladmin/businessdetails/addBusinessDetails`, formdata, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        console.log(response.data);
        if (response.data.success === true) {
          fetchrentalProfile();
          setIsEditingBusiness(false);
        }
      })
      .catch((e) => {
        console.log(e);
      });
  };

  const updateBussinessDetails = () => {
    const token = localStorage.getItem("authToken");
    const formdata = new FormData();
    formdata.append("id", idToUpdateBusinessDetails);
    formdata.append("srrentaladmin_id", srrentaladmin_id);
    formdata.append("businessname", businessname);
    formdata.append("description", description);
    formdata.append("aboutthisshop", aboutthisshop);
    formdata.append("address", address);
    formdata.append("phonenumber", businessPhone);
    formdata.append("businessemail", businessemail);
    formdata.append("cnic", cnic);
    if (shopLogo) {
      formdata.append("logo", shopLogo);
    }

    axios
      .post(
        `${baseUrl}/rentaladmin/businessdetails/updateBusinessDetails`,
        formdata,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then((response) => {
        console.log(response.data);
        if (response.data.success === true) {
          fetchrentalProfile();
          setIsEditingBusiness(false);
        }
      })
      .catch((e) => {
        console.log(e);
      });
  };

  // ── KYC Submit ──
  const uploadkycDocument = () => {
    if (!uploadedFile) {
      Swal.fire({
        icon: "warning",
        title: "No File Selected",
        text: "Please upload your KYC documents PDF first.",
      });
      return;
    }

    const token = localStorage.getItem("authToken");
    const formData = new FormData();
    formData.append("document", uploadedFile);

    axios
      .post(`${baseUrl}/rentaladmin/kyc/uploadkycdocument`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        console.log(response.data);
        if (response.data.success === true) {
          setKycStatus("submitted");
          Swal.fire({
            icon: "success",
            title: "KYC Submitted",
            text: "Your KYC documents have been submitted successfully! We will review them within 2-3 business days.",
          });
        }
      })
      .catch(() => {
        Swal.fire({
          icon: "error",
          title: "Submission Failed",
          text: "There was an error submitting your KYC documents. Please try again.",
        });
      });
  };

  // ── Change Password ──
  const changePassword = () => {
    if (
      !passwordData.currentPassword ||
      !passwordData.newPassword ||
      !passwordData.confirmPassword
    ) {
      Swal.fire({
        icon: "warning",
        title: "Missing Fields",
        text: "Please fill all password fields!",
      });
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      Swal.fire({
        icon: "warning",
        title: "Mismatch",
        text: "Passwords do not match!",
      });
      return;
    }

    const token = localStorage.getItem("authToken");
    const formData = new FormData();
    formData.append("currentPassword", passwordData.currentPassword);
    formData.append("newPassword", passwordData.newPassword);

    axios
      .post(`${baseUrl}/rentaladmin/profile/changePassword`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        console.log(response.data);
        if (response.data.success === true) {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "Password changed successfully!",
          });
          setShowPasswordModal(false);
          setPasswordData({
            currentPassword: "",
            newPassword: "",
            confirmPassword: "",
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.message || "Failed to change password.",
          });
        }
      })
      .catch((error) => {
        console.log(error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to change password.",
        });
      });
  };

  // ── Change Notification Settings ──
  const changeNotificationSetting = () => {
    const token = localStorage.getItem("authToken");
    const formData = new FormData();
    formData.append("emailNotification", notifications.emailNotifications ? 1 : 0);
    formData.append("inAppNotification", notifications.smsNotifications ? 1 : 0);

    axios
      .post(`${baseUrl}/rentaladmin/profile/changeNotificationSetting`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        console.log(response.data);
        if (response.data.success === true) {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "Notification settings updated!",
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.message || "Failed to update notification settings.",
          });
        }
      })
      .catch((error) => {
        console.log(error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to update notification settings.",
        });
      });
  };

  useEffect(() => {
    fetchrentalProfile();
  }, []);

  // ================================================================
  // HANDLERS
  // ================================================================

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
  };

  const handleNotificationToggle = (name) => {
    setNotifications((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const handleCancelBusiness = () => {
    setBusinessSettings(businessSettingsBackup);
    setShopLogo(shopLogoBackup);
    setShopLogoPreview(shopLogoPreviewBackup);
    setIsEditingBusiness(false);
  };

  const handleAvatarUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith("image/")) {
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setAvatarPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith("image/")) {
      setShopLogo(file);
      const reader = new FileReader();
      reader.onloadend = () => setShopLogoPreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      alert("Please upload a valid image file (JPG, PNG, GIF, etc.).");
    }
  };

  const handlePdfUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.type === "application/pdf") {
      setUploadedFile(file);
    } else {
      alert("Please upload a PDF file only.");
    }
  };

  const handleSaveProfile = () => {
    updateRentalProfile();
    setIsEditing(false);
  };

  const handleSaveNotifications = () => {
    changeNotificationSetting();
  };

  const handleSaveBusinessSettings = () => {
    if (businessDetails) {
      updateBussinessDetails();
    } else {
      addBussinessDetails();
    }
    setIsEditingBusiness(false);
  };

  const handleChangePassword = () => {
    changePassword();
  };

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [notifications, setNotifications] = useState({
    emailNotifications: true,
    smsNotifications: false,
    orderUpdates: true,
    promotions: true,
    newsletter: false,
    bookingAlerts: true,
    paymentAlerts: true,
    maintenanceReminders: true,
  });

  const [businessSettings, setBusinessSettings] = useState();

  // ================================================================
  // RETURN
  // ================================================================

  return (
    <RentalAdminLayout>
      <Container fluid className="profile-page">
        <div className="page-header">
          <h2>My Profile</h2>
          <p>Manage your account information</p>
        </div>

        <Row>
          {/* ════════════════════════════════
              Left Side — Profile Card
          ════════════════════════════════ */}
          <Col lg={4} md={12}>
            <div className="profile-card">
              <div className="profile-avatar-section">
                <input
                  type="file"
                  accept="image/*"
                  ref={avatarInputRef}
                  onChange={handleAvatarUpload}
                  style={{ display: "none" }}
                />

                <div className="profile-avatar-large">
                  <img
                    src={
                      avatarPreview
                        ? avatarPreview
                        : image
                        ? `${baseUrlForFile}/${image}`
                        : null
                    }
                    alt="profile"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: "50%",
                      display: image || avatarPreview ? "block" : "none",
                    }}
                  />
                  {!image && !avatarPreview && (
                    <span>{fullname?.charAt(0)}</span>
                  )}
                </div>

                <button
                  className="change-avatar-btn"
                  onClick={() => avatarInputRef.current.click()}
                >
                  <MdCamera /> Change Photo
                </button>
              </div>

              <div className="profile-info-summary">
                <h3>{fullname}</h3>
                <p className="email">{email}</p>
                <p className="phone">{businessPhone}</p>
              </div>

              {/* <div className="profile-stats">
                <div className="stat-item">
                  <span className="stat-value">15</span>
                  <span className="stat-label">Total Rentals</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">4</span>
                  <span className="stat-label">Active Rentals</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">7</span>
                  <span className="stat-label">Critical Alerts</span>
                </div>
              </div> */}

              <button
                className="change-password-btn"
                onClick={() => setShowPasswordModal(true)}
              >
                Change Password
              </button>
            </div>
          </Col>

          {/* ════════════════════════════════
              Right Side — Tabs
          ════════════════════════════════ */}
          <Col lg={8} md={12}>
            <div className="profile-details-card">
              {/* Tab Navigation */}
              <Nav variant="tabs" className="profile-tabs">
                <Nav.Item>
                  <Nav.Link
                    active={activeTab === "personal"}
                    onClick={() => setActiveTab("personal")}
                    className="profile-tab-link"
                  >
                    <MdPerson /> Personal Info
                  </Nav.Link>
                </Nav.Item>

                <Nav.Item>
                  <Nav.Link
                    active={activeTab === "business"}
                    onClick={() => setActiveTab("business")}
                    className="profile-tab-link"
                  >
                    <MdBusiness /> Business Settings
                  </Nav.Link>
                </Nav.Item>

                <Nav.Item>
                  <Nav.Link
                    active={activeTab === "notifications"}
                    onClick={() => setActiveTab("notifications")}
                    className="profile-tab-link"
                  >
                    <MdNotifications /> Notifications
                  </Nav.Link>
                </Nav.Item>

                <Nav.Item>
                  <Nav.Link
                    active={activeTab === "kyc"}
                    onClick={() => setActiveTab("kyc")}
                    className="profile-tab-link kyc-tab-link"
                  >
                    <MdVerifiedUser /> Kyc Verification
                  </Nav.Link>
                </Nav.Item>
              </Nav>

              {/* Tab Content */}
              <div className="tab-content-wrapper">

                {/* ── Personal Info Tab ── */}
                {activeTab === "personal" && (
                  <div className="tab-content-section">
                    <div className="card-header">
                      <h3>Personal Information</h3>
                      {!isEditing ? (
                        <button
                          className="edit-btn"
                          onClick={() => setIsEditing(true)}
                        >
                          <MdEdit /> Edit Profile
                        </button>
                      ) : (
                        <div className="action-buttons">
                          <button
                            className="cancel-btn"
                            onClick={() => setIsEditing(false)}
                          >
                            <MdCancel /> Cancel
                          </button>
                          <button
                            className="save-btn"
                            onClick={handleSaveProfile}
                          >
                            <MdSave /> Save Changes
                          </button>
                        </div>
                      )}
                    </div>
                    <Form>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Full Name</Form.Label>
                            <Form.Control
                              type="text"
                              name="fullname"
                              value={fullname}
                              onChange={(e) => setFullname(e.target.value)}
                              disabled={!isEditing}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Email Address</Form.Label>
                            <Form.Control
                              type="email"
                              name="email"
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              disabled={!isEditing}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Phone Number</Form.Label>
                            <Form.Control
                              type="text"
                              name="phonenumber"
                              value={cphonenumber}
                              onChange={(e) => setCPhonenumber(e.target.value)}
                              disabled={!isEditing}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={12}>
                          <Form.Group className="mb-3">
                            <Form.Label>Address</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={2}
                              name="address"
                              value={caddress}
                              onChange={(e) => setCAddress(e.target.value)}
                              disabled={!isEditing}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </Form>
                  </div>
                )}

                {/* ── Business Info Tab ── */}
                {activeTab === "business" && (
                  <div className="tab-content-section">
                    <div className="card-header">
                      <h3>Business Information</h3>
                      {!isEditingBusiness ? (
                        <button
                          className="edit-btn"
                          onClick={() => setIsEditingBusiness(true)}
                        >
                          <MdEdit /> Edit Business Info
                        </button>
                      ) : (
                        <div className="action-buttons">
                          <button
                            className="cancel-btn"
                            onClick={handleCancelBusiness}
                          >
                            <MdCancel /> Cancel
                          </button>
                          <button
                            className="save-btn"
                            onClick={handleSaveBusinessSettings}
                          >
                            <MdSave /> Save Settings
                          </button>
                        </div>
                      )}
                    </div>

                    {/* ── Shop Logo Section ── */}
                    <div className="kyc-section-card">
                      <h4 className="kyc-section-title">🏪 Shop Logo</h4>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "24px",
                          flexWrap: "wrap",
                        }}
                      >
                        <div
                          style={{
                            width: "110px",
                            height: "110px",
                            borderRadius: "50%",
                            overflow: "hidden",
                            border: "3px solid #FDB714",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: "#f8f9fa",
                          }}
                        >
                          {shopLogoPreview ? (
                            <img
                              src={shopLogoPreview}
                              alt="Shop Logo"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                borderRadius: "50%",
                              }}
                            />
                          ) : (
                            <span style={{ fontSize: "36px", color: "#aaa" }}>🏪</span>
                          )}
                        </div>

                        <div>
                          <p
                            style={{
                              margin: "0 0 8px",
                              fontSize: "13px",
                              color: isEditingBusiness ? "#555" : "#aaa",
                            }}
                          >
                            Upload your shop logo. Recommended size:{" "}
                            <strong>200×200px</strong>.<br />
                            Supported formats: JPG, PNG, GIF, SVG.
                          </p>
                          <input
                            type="file"
                            accept="image/*"
                            ref={logoInputRef}
                            onChange={handleLogoUpload}
                            style={{ display: "none" }}
                            disabled={!isEditingBusiness}
                          />
                          <button
                            type="button"
                            className="kyc-choose-btn"
                            onClick={() =>
                              isEditingBusiness && logoInputRef.current.click()
                            }
                            style={{
                              marginRight: "10px",
                              opacity: isEditingBusiness ? 1 : 0.45,
                              cursor: isEditingBusiness ? "pointer" : "not-allowed",
                            }}
                          >
                            <MdUpload size={16} />{" "}
                            {shopLogo ? "Change Logo" : "Upload Logo"}
                          </button>
                          {shopLogo && isEditingBusiness && (
                            <button
                              type="button"
                              className="cancel-btn"
                              style={{ fontSize: "13px", padding: "6px 14px" }}
                              onClick={() => {
                                setShopLogo(null);
                                setShopLogoPreview(null);
                              }}
                            >
                              <MdCancel size={15} /> Remove
                            </button>
                          )}
                          {shopLogo && (
                            <p style={{ marginTop: "6px", fontSize: "12px", color: "#4caf50" }}>
                              ✅ {shopLogo.name} ({(shopLogo.size / 1024).toFixed(1)} KB)
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* ── Business Form Fields ── */}
                    <div className="kyc-section-card">
                      <Form>
                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Business Name</Form.Label>
                              <Form.Control
                                type="text"
                                name="businessName"
                                value={businessname}
                                onChange={(e) => setBusinessName(e.target.value)}
                                disabled={!isEditingBusiness}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Business Email</Form.Label>
                              <Form.Control
                                type="email"
                                name="businessEmail"
                                value={businessemail}
                                onChange={(e) => setBusinessEmail(e.target.value)}
                                disabled={!isEditingBusiness}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Business Phone</Form.Label>
                              <Form.Control
                                type="text"
                                name="businessPhone"
                                value={businessPhone}
                                onChange={(e) => setBusinessPhone(e.target.value)}
                                disabled={!isEditingBusiness}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={12}>
                            <Form.Group className="mb-3">
                              <Form.Label>Business Address</Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={2}
                                name="businessAddress"
                                value={address}
                                onChange={(e) => setAddress(e.target.value)}
                                disabled={!isEditingBusiness}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Description</Form.Label>
                              <Form.Control
                                type="text"
                                name="description"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                disabled={!isEditingBusiness}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={12}>
                            <Form.Group className="mb-3">
                              <Form.Label>About This Shop</Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={4}
                                name="aboutShop"
                                value={aboutthisshop}
                                onChange={(e) => setAboutThisShop(e.target.value)}
                                disabled={!isEditingBusiness}
                                placeholder="Describe your shop — what equipment you offer, your speciality, years of experience, service area, etc."
                              />
                              <Form.Text className="text-muted">
                                This description will be visible to customers on your shop profile page.
                              </Form.Text>
                            </Form.Group>
                          </Col>
                        </Row>
                      </Form>
                    </div>
                  </div>
                )}

                {/* ── Notifications Tab ── */}
                {activeTab === "notifications" && (
                  <div className="tab-content-section">
                    <div className="card-header">
                      <h3>Notification Preferences</h3>
                    </div>
                    <div className="notification-list">
                      {[
                        {
                          key: "emailNotifications",
                          title: "Email Notifications",
                          desc: "Receive notifications via email",
                        },
                        {
                          key: "smsNotifications",
                          title: "SMS Notifications",
                          desc: "Receive text message alerts",
                        },
                      ].map((item) => (
                        <div key={item.key} className="notification-item">
                          <div className="notification-info">
                            <h4>{item.title}</h4>
                            <p>{item.desc}</p>
                          </div>
                          <Form.Check
                            type="switch"
                            checked={notifications[item.key]}
                            onChange={() => handleNotificationToggle(item.key)}
                            className="notification-toggle"
                          />
                        </div>
                      ))}
                    </div>
                    <button
                      className="save-preferences-btn"
                      onClick={handleSaveNotifications}
                    >
                      <MdSave /> Save Preferences
                    </button>
                  </div>
                )}

                {/* ── KYC Verification Tab ── */}
                {activeTab === "kyc" && (
                  <div className="tab-content-section">
                    <div className="card-header">
                      <h3>KYC Verification</h3>
                    </div>

                    {/* ── Dynamic Status Banner ── */}
                    <div className={`kyc-status-banner ${currentStatus.cls}`}>
                      <div className="kyc-status-left">
                        <div className="kyc-status-icon">{currentStatus.icon}</div>
                        <div>
                          <h4>{currentStatus.title}</h4>
                          <p>{currentStatus.desc}</p>
                        </div>
                      </div>
                      <span className="kyc-status-badge">{currentStatus.badge}</span>
                    </div>

                    <div className="kyc-section-card">
                      <h4 className="kyc-section-title">📋 Upload Instructions:</h4>
                      <Row>
                        <Col md={6}>
                          <div className="kyc-instructions-box">
                            <p className="kyc-instructions-heading">✅ Required Documents:</p>
                            <ul className="kyc-instructions-list">
                              <li><strong>CNIC Front Side</strong> - Clear &amp; readable</li>
                              <li><strong>CNIC Back Side</strong> - Clear &amp; readable</li>
                              <li><strong>Business License/Registration</strong> - Official document</li>
                              <li>CNIC must be <strong>valid and not expired</strong></li>
                              <li>All 3 documents <strong>must be included</strong></li>
                            </ul>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="kyc-instructions-box">
                            <p className="kyc-instructions-heading">📎 File Specifications:</p>
                            <ul className="kyc-instructions-list">
                              <li>Format: <strong>PDF only</strong></li>
                              <li>Maximum size: <strong>20 MB</strong></li>
                              <li>Combine all pages into <strong>single PDF</strong></li>
                              <li>Recommended: Scan at <strong>300 DPI</strong></li>
                              <li>Do not password protect the PDF</li>
                            </ul>
                          </div>
                        </Col>
                      </Row>
                    </div>

                    <div className="kyc-section-card">
                      <h4 className="kyc-section-title">
                        KYC Verification (Single PDF File)
                      </h4>
                      <p className="kyc-upload-subtitle">
                        Upload a single PDF file containing all required documents
                      </p>
                      <div
                        className={`kyc-upload-box ${uploadedFile ? "has-file" : ""}`}
                        onClick={() => pdfInputRef.current.click()}
                      >
                        <input
                          type="file"
                          accept="application/pdf"
                          ref={pdfInputRef}
                          onChange={handlePdfUpload}
                          style={{ display: "none" }}
                        />
                        <div className="kyc-upload-icon">
                          <MdUpload size={40} />
                        </div>
                        {uploadedFile ? (
                          <>
                            <p className="kyc-upload-title" style={{ color: "#2e7d32" }}>
                              ✅ {uploadedFile.name}
                            </p>
                            <p className="kyc-upload-size">
                              {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                          </>
                        ) : (
                          <>
                            <p className="kyc-upload-title">Upload KYC Documents PDF</p>
                            <p className="kyc-upload-size">Maximum File size: 20MB</p>
                          </>
                        )}
                        <button
                          className="kyc-choose-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            pdfInputRef.current.click();
                          }}
                        >
                          <MdUpload size={16} />
                          {uploadedFile ? "Change PDF File" : "Choose PDF File"}
                        </button>
                      </div>
                    </div>

                    <div className="kyc-section-card">
                      <h4 className="kyc-section-title">⏱ Verification Process:</h4>
                      <div className="kyc-steps">
                        {[
                          {
                            step: 1,
                            title: "Upload Documents",
                            desc: "Upload your PDF File with all required documents",
                          },
                          {
                            step: 2,
                            title: "Under Review",
                            desc: "Our team will verify your documents (2-3 business days)",
                          },
                          {
                            step: 3,
                            title: "Verification Complete",
                            desc: "You will receive email notification once approved",
                          },
                        ].map((s, i) => (
                          <div key={i} className="kyc-step-item">
                            <div className="kyc-step-circle">{s.step}</div>
                            {i < 2 && <div className="kyc-step-line" />}
                            <div className="kyc-step-content">
                              <h5>{s.title}</h5>
                              <p>{s.desc}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="kyc-submit-wrapper">
                      <button
                        className="kyc-submit-btn"
                        onClick={uploadkycDocument}
                        disabled={kycStatus === "submitted" || kycStatus === "approved"}
                      >
                        <MdSave size={18} />
                        {kycStatus === "submitted"
                          ? "Already Submitted"
                          : kycStatus === "approved"
                          ? "Verified ✓"
                          : "Submit for Verification"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Col>
        </Row>

        {/* ════════════════════════════════
            Change Password Modal
        ════════════════════════════════ */}
        <Modal
          show={showPasswordModal}
          onHide={() => setShowPasswordModal(false)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Change Password</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Current Password</Form.Label>
                <Form.Control
                  type="password"
                  name="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  placeholder="Enter current password"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>New Password</Form.Label>
                <Form.Control
                  type="password"
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  placeholder="Enter new password"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Confirm New Password</Form.Label>
                <Form.Control
                  type="password"
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  placeholder="Confirm new password"
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <button
              className="modal-cancel-btn"
              onClick={() => setShowPasswordModal(false)}
            >
              Cancel
            </button>
            <button className="modal-confirm-btn" onClick={handleChangePassword}>
              Change Password
            </button>
          </Modal.Footer>
        </Modal>
      </Container>
    </RentalAdminLayout>
  );
};

export default Profile;